import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Criar o contexto do carrinho
const CartContext = createContext();

// Reducer para gerenciar o estado do carrinho
const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      const existingItem = state.items.find(item => item._id === action.payload._id);
      
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item._id === action.payload._id
              ? { ...item, quantidade: item.quantidade + 1 }
              : item
          ),
          total: state.total + action.payload.preco
        };
      } else {
        return {
          ...state,
          items: [...state.items, { ...action.payload, quantidade: 1 }],
          total: state.total + action.payload.preco,
          itemCount: state.itemCount + 1
        };
      }
      
    case 'REMOVE_FROM_CART':
      const itemToRemove = state.items.find(item => item._id === action.payload);
      return {
        ...state,
        items: state.items.filter(item => item._id !== action.payload),
        total: state.total - (itemToRemove ? itemToRemove.preco * itemToRemove.quantidade : 0),
        itemCount: state.itemCount - (itemToRemove ? itemToRemove.quantidade : 0)
      };
      
    case 'UPDATE_QUANTITY':
      const itemToUpdate = state.items.find(item => item._id === action.payload.id);
      const quantityDifference = action.payload.quantity - (itemToUpdate ? itemToUpdate.quantidade : 0);
      
      return {
        ...state,
        items: state.items.map(item =>
          item._id === action.payload.id
            ? { ...item, quantidade: action.payload.quantity }
            : item
        ),
        total: state.total + (quantityDifference * itemToUpdate.preco),
        itemCount: state.itemCount + quantityDifference
      };
      
    case 'CLEAR_CART':
      return {
        items: [],
        total: 0,
        itemCount: 0
      };
      
    case 'LOAD_CART':
      // Calcular totais ao carregar o carrinho
      const total = action.payload.reduce((sum, item) => sum + (item.preco * item.quantidade), 0);
      const itemCount = action.payload.reduce((sum, item) => sum + item.quantidade, 0);
      
      return {
        items: action.payload,
        total,
        itemCount
      };
      
    default:
      return state;
  }
};

// Provedor do contexto do carrinho
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, { items: [], total: 0, itemCount: 0 });

  // Carregar carrinho do localStorage ao iniciar
  useEffect(() => {
    const cartData = localStorage.getItem('cart');
    if (cartData) {
      dispatch({ type: 'LOAD_CART', payload: JSON.parse(cartData) });
    }
  }, []);

  // Salvar carrinho no localStorage quando mudar
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(state.items));
  }, [state.items]);

  // Adicionar item ao carrinho
  const addToCart = (produto) => {
    dispatch({ type: 'ADD_TO_CART', payload: produto });
  };

  // Remover item do carrinho
  const removeFromCart = (produtoId) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: produtoId });
  };

  // Atualizar quantidade de um item
  const updateQuantity = (produtoId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(produtoId);
    } else {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { id: produtoId, quantity } });
    }
  };

  // Limpar carrinho
  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  // Valor do contexto
  const value = {
    cart: state,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Hook personalizado para usar o carrinho
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart deve ser usado dentro de um CartProvider');
  }
  return context;
};